interface LogoProps {
  isScrolled?: boolean;
  variant?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

interface LightningBoltProps {
  className?: string;
  withGlow?: boolean;
}

export const LightningBolt = ({ className, withGlow = true }: LightningBoltProps) => (
  <svg
    viewBox="0 0 32 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="boltGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFDD00" />
        <stop offset="50%" stopColor="#FFD700" />
        <stop offset="100%" stopColor="#FFCC00" />
      </linearGradient>
      {withGlow && (
        <>
          <filter id="boltGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="boltGlowHover" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </>
      )}
    </defs>
    <path
      d="M20 0L6 22H14L10 48L28 18H18L20 0Z"
      fill="url(#boltGradient)"
      filter={withGlow ? "url(#boltGlow)" : undefined}
      className="lightning-path"
    />
  </svg>
);

const Logo = ({ isScrolled = true, variant, size = 'md', className = '' }: LogoProps) => {
  // Determine colors based on variant or isScrolled
  const isDark = variant === 'dark' || (!variant && !isScrolled);
  const textColor = isDark ? 'text-white' : 'text-foreground';
  const subTextColor = isDark ? 'text-white/70' : 'text-muted-foreground';

  // Size classes
  const sizeClasses = {
    sm: {
      text: 'text-xl md:text-2xl',
      bolt: 'h-8 w-5 md:h-9 md:w-5',
      servis: 'text-[8px] md:text-[10px]',
    },
    md: {
      text: 'text-2xl md:text-3xl lg:text-4xl',
      bolt: 'h-10 w-7 md:h-12 md:w-7 lg:h-14 lg:w-8',
      servis: 'text-[9px] md:text-xs lg:text-sm',
    },
    lg: {
      text: 'text-3xl md:text-4xl lg:text-5xl',
      bolt: 'h-12 w-8 md:h-14 md:w-9 lg:h-16 lg:w-10',
      servis: 'text-[10px] md:text-sm lg:text-base',
    },
  };

  const classes = sizeClasses[size];

  return (
    <div 
      className={`inline-flex flex-col items-start leading-none group cursor-pointer min-w-fit ${className}`}
      aria-label="ESKO Servis"
    >
      {/* Main row: ES ⚡ KO */}
      <div className="flex items-center">
        <span className={`${classes.text} font-black tracking-tight ${textColor}`}>
          ES
        </span>
        <LightningBolt 
          className={`${classes.bolt} -mx-0.5 relative -top-0.5 
            bolt-pulse
            transition-all duration-300 ease-out
            group-hover:scale-115 group-hover:drop-shadow-[0_0_12px_#FFD700]`}
        />
        <span className={`${classes.text} font-black tracking-tight ${textColor}`}>
          KO
        </span>
      </div>
      {/* SERVIS text aligned under KO - fixed width container */}
      <div className="flex justify-end w-full -mt-1 pr-0.5">
        <span className={`${classes.servis} font-bold tracking-[0.2em] uppercase ${subTextColor}`}>
          SERVIS
        </span>
      </div>
    </div>
  );
};

export default Logo;
